// components
import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';
import { DefaultFallback } from '../../common/taxesAndFeesComponents';
import TaxItemRateField from './TaxItemRateField';

// types
import type { AcceleronTaxItem, paymentServicesTypes, OfferType } from '@makemydeal/dr-platform-types';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';

// styles
import { StyledMonthlyRate } from './MonthlyTaxSummary.style';

import { useDispatch, useSelector } from 'react-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { actionCreators } from '@makemydeal/dr-offer-redux';
import { TAXRATE_TOTAL_THRESHOLD } from '../constants';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

interface TaxItemTotalRateFieldProps {
    taxItem: paymentServicesTypes.TaxItem;
    taxOverrideEnabled?: boolean;
    offerType: OfferType;
}

export default function TaxTotalRateField({ taxItem, taxOverrideEnabled, offerType }: TaxItemTotalRateFieldProps) {
    const dispatch = useDispatch();
    const isPercentTaxRatesEnabled = useSelector(featureToggleSelectors.isPercentTaxRatesEnabled);

    const hasPositiveRateInTaxBreakdown = (taxBreakdown?: Record<string, number>): boolean => {
        if (!taxBreakdown) return false;

        return Object.entries(taxBreakdown).some(([key, value]) => key.includes('Rate') && value > 0);
    };

    const handleBlur = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        const fieldName = 'taxRate';
        const newValue = !isNaN(parseFloat(event.target.value)) ? parseFloat(event.target.value) : 0;

        if (taxItem[fieldName] === newValue) {
            return;
        }

        const updatedTaxItem = {
            ...taxItem,
            manualChanges: {
                taxRate: true
            },
            // we need to convert rate from percent to decimal format, to have consistent data format between PS and dxg
            [fieldName]: isPercentTaxRatesEnabled ? newValue : Number((newValue / 100).toFixed(5)),
            isManualTax: true,
            isManualTaxRate: true,
            isManualTaxAmount: false,
            taxBreakdown: undefined
        };

        const isAmountExceedThreshold = newValue > TAXRATE_TOTAL_THRESHOLD;

        if (isAmountExceedThreshold) {
            return;
        }
        dispatch(actionCreators.updateTaxItems([updatedTaxItem], offerType, true));
        dispatch(deskingActionCreators.hideTotalTaxRateSupersedeAlert());
    };

    const hasManualTotalTax = useSelector(offerReduxSelectors.hasManualTotalTax);
    const isRateReadable = hasManualTotalTax || taxItem.manualChanges?.taxAmount;
    const isRateInTaxBreakdown = hasPositiveRateInTaxBreakdown(taxItem.taxBreakdown as Record<string, number> | undefined);

    if (taxOverrideEnabled) {
        return (
            <Box display="grid" textAlign="end" data-testid="total-rate">
                <Typography variant="label-md" color="sem.color.on-surface.muted">
                    Rate
                </Typography>

                {hasManualTotalTax ? (
                    <StyledMonthlyRate $textAlign="end" $minWidth="11rem">
                        <DefaultFallback />
                    </StyledMonthlyRate>
                ) : (
                    <Typography data-testid="total-rate-value" variant="body-md" tag="span">
                        {taxItem.taxRate}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box
            display="grid"
            marginLeft={isRateReadable ? '1rem' : ''}
            textAlign="start"
            alignContent="flex-start"
            {...(isRateReadable && { lineHeight: '2.8125rem' })}
        >
            {isRateReadable ? (
                <StyledMonthlyRate>
                    <DefaultFallback />
                </StyledMonthlyRate>
            ) : (
                <TaxItemRateField
                    data-testid="total-rate-input"
                    data={{
                        isEditedRate: (taxItem as AcceleronTaxItem).isManualTaxRate || taxItem.manualChanges?.taxRate,
                        rate: taxItem.taxRate,
                        isDisabled: isRateInTaxBreakdown,
                        name: 'totalTaxRate',
                        hasError: false,
                        key: 'totalTaxRate'
                    }}
                    handleBlur={handleBlur}
                    label={isRateInTaxBreakdown ? 'Total Rate' : 'Rate'}
                    key={taxItem.taxRate}
                    maxValue={99}
                />
            )}
        </Box>
    );
}
